import { useEffect, useState, useReducer } from 'react'
import { I18n } from 'aws-amplify'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { object, string } from 'yup'

import { createCity, requestCity, updateCity } from '@/actions/operations/city'

import { requestAllCountries } from '@/actions/operations/country'
import { requestAllRegions } from '@/actions/operations/region'

import {
  getOperationsCity,
  getOperationsAllCountries,
  getOperationsAllRegions,
  getOperationsLoading,
  getOperationsError
} from '@/reducers/selectors'

import { Flex, Form, Input, Label, Select, Text, Loader } from '@/primitives'

import InputError from '@/elements/InputError'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import OrganizationInput from '../../Shared/Organization/Input'

import { getValidationErrorMap } from '@/Util/GeneralUtils'
import history from '../../../../history'

import { formInitalState, formReducer } from './state'

const SCHEMA = object({
  organizationId: string().required(I18n.get('An organization is required.')),
  name: string().required(I18n.get('Name is required.')),
  countryId: string().required(I18n.get('A country must be selected.')),
  regionId: string().required(I18n.get('A region must be selected.'))
})

function CityForm({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()
  const { itemId } = useParams()

  const city = getOperationsCity()
  const countries = getOperationsAllCountries()
  const regions = getOperationsAllRegions()
  const loading = getOperationsLoading()
  const error = getOperationsError()

  const [formState, dispatchFormState] = useReducer(
    formReducer,
    formInitalState
  )

  const [errors, setErrors] = useState({})
  const [waiting, setWaiting] = useState(false)

  useEffect(() => {
    dispatch(requestAllCountries())
    dispatch(requestAllRegions())
  }, [dispatch])

  useEffect(() => {
    if (itemId && city.id !== itemId) {
      dispatch(requestCity({ cityId: itemId }))
    }
  }, [dispatch, city?.id, itemId])

  useEffect(() => {
    if (city?.id && city.id === itemId) {
      dispatchFormState({ type: 'set-state', state: city })
    }
  }, [city, itemId])

  useEffect(() => {
    if (!itemId && state.organizations.length === 1) {
      dispatchFormState({
        type: 'update',
        name: 'organizationId',
        value: state.organizations[0]
      })
    }
  }, [state.organizations, itemId])

  useEffect(() => {
    if (!itemId && state.countries.length === 1) {
      dispatchFormState({
        type: 'update',
        name: 'countryId',
        value: state.countries[0]
      })
    }
  }, [state.countries, itemId])

  useEffect(() => {
    if (!itemId && state.regions.length === 1) {
      dispatchFormState({
        type: 'update',
        name: 'regionId',
        value: state.regions[0]
      })
    }
  }, [state.regions, itemId])

  useEffect(() => {
    if (waiting && !loading && !error) {
      history.replace(modulePath)
    }
  }, [waiting, loading, error, modulePath])

  const onSubmit = async e => {
    e?.preventDefault()
    try {
      const payload = await SCHEMA.validate(formState, { abortEarly: false })
      setWaiting(true)

      if (itemId) {
        payload.cityId = itemId
        dispatch(updateCity(payload))
      } else {
        dispatch(createCity(payload))
      }
    } catch (err) {
      setErrors(getValidationErrorMap(err))
    }
  }

  const onChange = e => {
    let { name, value } = e.currentTarget
    dispatchFormState({ type: 'update', name, value })
  }

  const handleOrganizationInput = organizationId => {
    dispatchFormState({
      type: 'set-state',
      state: {
        ...formState,
        organizationId,
        countryId: '',
        regionId: ''
      }
    })
  }

  const onChangeCountry = e => {
    dispatchFormState({
      type: 'set-state',
      state: {
        ...formState,
        countryId: e.currentTarget.value,
        regionId: ''
      }
    })
  }

  const filterCountries = () => {
    return countries.filter(c => c.organizationId === formState.organizationId)
  }

  const filterRegions = () => {
    return regions.filter(r => r.countryId === formState.countryId)
  }

  const regionOptions = filterRegions()

  return (
    <Form className='Operations__Form'>
      <HeaderV2
        title={I18n.get('Manage City')}
        backPath={modulePath}
        buttonText={I18n.get('Save City')}
        buttonCallback={onSubmit}
        buttonIcon={'save'}
      />
      <Loader isLoading={loading}>
        <OrganizationInput
          fieldName={'organizationId'}
          organizationId={formState.organizationId}
          handleInput={handleOrganizationInput}
          errors={errors}
        />
        <Flex axisGap={300} className='Operations__Form__Fields'>
          <Label>
            <Text variant='page' tone={700}>
              {I18n.get('Country')}
            </Text>
            <Select
              className='Operations__Form__Select'
              name='countryId'
              value={formState.countryId}
              onChange={onChangeCountry}
            >
              <option default value=''>
                {I18n.get('Please select')}
              </option>
              {filterCountries().map(({ name, id }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
            <InputError error={errors?.countryId} />
          </Label>
          <Label>
            <Text variant='page' tone={700}>
              {I18n.get('Region')}
            </Text>
            <Select
              className='Operations__Form__Select'
              name='regionId'
              value={formState.regionId}
              onChange={onChange}
              disabled={regionOptions.length === 0}
            >
              <option default value=''>
                {I18n.get('Please select')}
              </option>
              {regionOptions.map(({ name, id }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
            <InputError error={errors?.regionId} />
          </Label>
        </Flex>
        <Flex axisGap={300} className='Operations__Form__Fields'>
          <Label>
            <Text variant='page' tone={700}>
              {I18n.get('Name')}
            </Text>
            <Input value={formState.name} name='name' onChange={onChange} />
            <InputError error={errors?.name} />
          </Label>
        </Flex>
      </Loader>
    </Form>
  )
}

export default CityForm
